/**
 * useReviewComments Hook
 *
 * Manages review comments from permit reviewers:
 * - CRUD operations for review comments
 * - Status tracking (open, in_progress, resolved)
 * - Assignment to team members
 * - Filtering by review cycle, source, status
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type {
  ReviewComment,
  ReviewCommentSource,
  ReviewCommentCategory,
  ReviewCommentStatus,
  ReviewCommentResolution,
} from '@/types/jurisdiction';

interface UseReviewCommentsReturn {
  comments: ReviewComment[];
  isLoading: boolean;
  error: string | null;

  // CRUD operations
  addComment: (
    comment: Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>
  ) => Promise<string>;
  updateComment: (id: string, updates: Partial<ReviewComment>) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;

  // Status updates
  updateStatus: (id: string, status: ReviewCommentStatus) => Promise<void>;
  resolveComment: (id: string, resolution: Omit<ReviewCommentResolution, 'resolvedAt' | 'resolvedBy'>) => Promise<void>;

  // Assignment
  assignComment: (id: string, userId: string, userName?: string) => Promise<void>;

  // Bulk operations
  importComments: (comments: Array<Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
}

interface UseReviewCommentsOptions {
  jobId: string;
}

export function useReviewComments({ jobId }: UseReviewCommentsOptions): UseReviewCommentsReturn {
  const { user, profile, loading: authLoading } = useAuth();
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get contractor ID - only use once profile is loaded to avoid permission issues
  const contractorId = profile?.contractorId || user?.uid;

  // Subscribe to review_comments subcollection
  useEffect(() => {
    // Wait for auth to finish loading before attempting to access the collection
    if (authLoading) {
      return;
    }

    if (!contractorId || !jobId) {
      setComments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const commentsRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/review_comments`);
    const q = query(commentsRef, orderBy('receivedAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const commentsData: ReviewComment[] = [];
        snapshot.forEach((doc) => {
          commentsData.push({
            id: doc.id,
            jobId,
            ...doc.data(),
          } as ReviewComment);
        });
        setComments(commentsData);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error fetching review comments:', err);
        setError('Failed to load review comments');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, contractorId, jobId]);

  // Add a new comment
  const addComment = useCallback(
    async (
      comment: Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>
    ): Promise<string> => {
      if (!contractorId || !user || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const commentsRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/review_comments`);
      const now = Timestamp.now();

      const docRef = await addDoc(commentsRef, {
        ...comment,
        jobId,
        createdAt: now,
        updatedAt: now,
      });

      return docRef.id;
    },
    [contractorId, user, jobId]
  );

  // Update an existing comment
  const updateComment = useCallback(
    async (id: string, updates: Partial<ReviewComment>): Promise<void> => {
      if (!contractorId || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const commentRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/review_comments`, id);

      await updateDoc(commentRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    },
    [contractorId, jobId]
  );

  // Delete a comment
  const deleteComment = useCallback(
    async (id: string): Promise<void> => {
      if (!contractorId || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const commentRef = doc(db, `contractors/${contractorId}/jobs/${jobId}/review_comments`, id);
      await deleteDoc(commentRef);
    },
    [contractorId, jobId]
  );

  // Update status
  const updateStatus = useCallback(
    async (id: string, status: ReviewCommentStatus): Promise<void> => {
      await updateComment(id, { status });
    },
    [updateComment]
  );

  // Resolve a comment
  const resolveComment = useCallback(
    async (
      id: string,
      resolution: Omit<ReviewCommentResolution, 'resolvedAt' | 'resolvedBy'>
    ): Promise<void> => {
      if (!user) {
        throw new Error('Not authenticated');
      }

      await updateComment(id, {
        status: 'resolved',
        resolution: {
          ...resolution,
          resolvedAt: new Date().toISOString(),
          resolvedBy: user.uid,
        },
      });
    },
    [updateComment, user]
  );

  // Assign a comment to a team member
  const assignComment = useCallback(
    async (id: string, userId: string, userName?: string): Promise<void> => {
      await updateComment(id, {
        assignedTo: userId,
        assignedToName: userName,
      });
    },
    [updateComment]
  );

  // Import multiple comments at once
  const importComments = useCallback(
    async (
      newComments: Array<Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>>
    ): Promise<void> => {
      if (!contractorId || !user || !jobId) {
        throw new Error('Not authenticated or job not specified');
      }

      const commentsRef = collection(db, `contractors/${contractorId}/jobs/${jobId}/review_comments`);
      const now = Timestamp.now();

      // Add all comments in parallel
      const promises = newComments.map((comment) =>
        addDoc(commentsRef, {
          ...comment,
          jobId,
          createdAt: now,
          updatedAt: now,
        })
      );

      await Promise.all(promises);
    },
    [contractorId, user, jobId]
  );

  return {
    comments,
    isLoading,
    error,
    addComment,
    updateComment,
    deleteComment,
    updateStatus,
    resolveComment,
    assignComment,
    importComments,
  };
}

// Helper function to create a new comment with defaults
export function createDefaultComment(
  overrides: Partial<Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'>> = {}
): Omit<ReviewComment, 'id' | 'jobId' | 'createdAt' | 'updatedAt'> {
  return {
    reviewCycle: 1,
    source: 'building_dept',
    category: 'plans_correction',
    status: 'open',
    originalText: '',
    receivedAt: new Date().toISOString(),
    ...overrides,
  };
}

// Helper to parse review cycle from a comment letter
export function parseReviewCycle(text: string): number {
  // Look for patterns like "1st review", "2nd review", "Review #1", etc.
  const patterns = [
    /(\d+)(?:st|nd|rd|th)\s*review/i,
    /review\s*#?\s*(\d+)/i,
    /cycle\s*#?\s*(\d+)/i,
    /round\s*#?\s*(\d+)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return parseInt(match[1], 10);
    }
  }

  return 1; // Default to first review cycle
}

// Helper to categorize comments based on content
export function categorizeComment(text: string): ReviewCommentCategory {
  const lowerText = text.toLowerCase();

  // Missing document patterns
  if (
    lowerText.includes('missing') ||
    lowerText.includes('not included') ||
    lowerText.includes('provide') ||
    lowerText.includes('submit')
  ) {
    if (
      lowerText.includes('document') ||
      lowerText.includes('certificate') ||
      lowerText.includes('form') ||
      lowerText.includes('drawing')
    ) {
      return 'missing_document';
    }
  }

  // Code violation patterns
  if (
    lowerText.includes('violation') ||
    lowerText.includes('code section') ||
    lowerText.includes('does not comply') ||
    lowerText.includes('not per code') ||
    lowerText.includes('fbc') ||
    lowerText.includes('nec')
  ) {
    return 'code_violation';
  }

  // Fee issues
  if (
    lowerText.includes('fee') ||
    lowerText.includes('payment') ||
    lowerText.includes('cost') ||
    lowerText.includes('charge')
  ) {
    return 'fee_issue';
  }

  // Site issues
  if (
    lowerText.includes('setback') ||
    lowerText.includes('site plan') ||
    lowerText.includes('lot') ||
    lowerText.includes('property line') ||
    lowerText.includes('easement')
  ) {
    return 'site_issue';
  }

  // Clarification
  if (
    lowerText.includes('clarify') ||
    lowerText.includes('unclear') ||
    lowerText.includes('explain') ||
    lowerText.includes('specify') ||
    lowerText.includes('?')
  ) {
    return 'clarification_needed';
  }

  // Approval conditions
  if (
    lowerText.includes('condition') ||
    lowerText.includes('prior to') ||
    lowerText.includes('before') ||
    lowerText.includes('upon')
  ) {
    return 'approval_condition';
  }

  // Default to plans correction for anything else
  return 'plans_correction';
}

// Helper to detect source department from text
export function detectSource(text: string): ReviewCommentSource {
  const lowerText = text.toLowerCase();

  if (lowerText.includes('fire') || lowerText.includes('nfpa')) {
    return 'fire_marshal';
  }
  if (
    lowerText.includes('zoning') ||
    lowerText.includes('setback') ||
    lowerText.includes('variance')
  ) {
    return 'zoning';
  }
  if (
    lowerText.includes('engineer') ||
    lowerText.includes('structural') ||
    lowerText.includes('civil')
  ) {
    return 'engineering';
  }
  if (
    lowerText.includes('environmental') ||
    lowerText.includes('stormwater') ||
    lowerText.includes('erosion')
  ) {
    return 'environmental';
  }
  if (
    lowerText.includes('utility') ||
    lowerText.includes('water') ||
    lowerText.includes('sewer')
  ) {
    return 'utilities';
  }
  if (lowerText.includes('hoa') || lowerText.includes('association')) {
    return 'hoa';
  }

  return 'building_dept';
}
